import { useEffect, useMemo, useState } from "react";
import {
  bulkArchiveChats,
  createTag,
  deleteTag,
  fetchMessages,
  fetchTags,
  markChatRead,
  syncChatMetadata,
} from "../api/whatsapp.js";
import { useAppActions } from "../app/AppProvider.tsx";
import { useModal } from "../components/ModalProvider.tsx";
import { useToast } from "../components/ToastProvider.tsx";
import type { ChatTag, WhatsappChat } from "../types.ts";
import { coalesceChatsForList, filterChats } from "./Chat.helpers.ts";
import { SvgIcon } from "./chat-list/ChatAvatar.tsx";
import { ChatContextMenu, type ContextMenuState } from "./chat-list/ChatContextMenu.tsx";
import { ChatListItem } from "./chat-list/ChatListItem.tsx";
import { FilterChips } from "./chat-list/FilterChips.tsx";
import { icons } from "./chat-list/icons.ts";

let metadataSyncStarted = false;
let tagsLoaded = false;

export function ChatList() {
  const { state, dispatch, patch } = useAppActions();
  const { showToast } = useToast();
  const modal = useModal();
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);

  const anyConnected = (state.whatsappSession?.status === "connected")
    || (state.accounts || []).some((a) => (a as { status?: string }).status === "connected");

  useEffect(() => {
    if (!anyConnected) return;
    if (!tagsLoaded) {
      tagsLoaded = true;
      fetchTags().catch(() => {
        tagsLoaded = false;
      });
    }
    if (metadataSyncStarted) return;
    metadataSyncStarted = true;
    syncChatMetadata().catch((err: unknown) => {
      console.warn("[wcrm] sync chat metadata failed", err);
      metadataSyncStarted = false;
    });
  }, [anyConnected]);

  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    window.setTimeout(() => document.addEventListener("click", close, { once: true }), 0);
    return () => document.removeEventListener("click", close);
  }, [contextMenu]);

  const activeFilter = state.chatListFilter || "all";
  const accountFilterSet = useMemo(() => new Set(state.accountFilter || []), [state.accountFilter]);
  const defaultsByProvider = useMemo(() => {
    const map = new Map<string, string>();
    for (const acc of state.accounts || []) {
      const provider = (acc.provider || "baileys") as string;
      if (acc.isDefault && acc.sessionId) map.set(provider, acc.sessionId);
    }
    for (const acc of state.accounts || []) {
      const provider = (acc.provider || "baileys") as string;
      if (!map.has(provider) && acc.sessionId) map.set(provider, acc.sessionId);
    }
    return map;
  }, [state.accounts]);

  function chatAccountId(chat: typeof state.chats[number]): string {
    const explicit = chat.accountSessionId || "";
    if (explicit) return explicit;
    const provider = chat.provider || "baileys";
    return defaultsByProvider.get(provider) || "";
  }

  const chats = useMemo(() => {
    const base = coalesceChatsForList(state.chats || []);
    const byAccount = accountFilterSet.size === 0
      ? base
      : base.filter((chat) => accountFilterSet.has(chatAccountId(chat)));
    return filterChats(byAccount, activeFilter, state.chatListSearch || "", state.chatTags || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.chats, accountFilterSet, activeFilter, state.chatListSearch, state.chatTags, defaultsByProvider]);

  function messagesForChatItem(chat: WhatsappChat) {
    const aliases = Array.isArray(chat.aliasJids)
      ? chat.aliasJids.map((jid) => String(jid || "").trim()).filter(Boolean)
      : [];
    return [chat.chatJid, ...aliases].flatMap((jid) => state.chatMessages[jid] || []);
  }

  function isActiveChatItem(chat: WhatsappChat) {
    if (state.activeChatJid === chat.chatJid) return true;
    const aliases = Array.isArray(chat.aliasJids) ? chat.aliasJids : [];
    return aliases.some((jid) => jid === state.activeChatJid);
  }

  async function openChat(chatJid: string) {
    if (state.activeChatJid === chatJid) return;
    patch({ activeChatJid: chatJid });
    if (!state.chatMessages[chatJid]) {
      patch({ chatLoading: { ...state.chatLoading, [chatJid]: true } });
      try {
        const chatRow = (state.chats || []).find((chat) => chat.chatJid === chatJid);
        const aliasJids = Array.isArray(chatRow?.aliasJids) ? chatRow.aliasJids : [];
        const messages = await fetchMessages(chatJid, { limit: 50, aliasJids });
        dispatch({ type: "SET_CHAT_MESSAGES", chatJid, messages });
      } catch (err) {
        showToast(err instanceof Error ? err.message : "Falha ao carregar conversa", "error");
      } finally {
        patch({ chatLoading: { ...state.chatLoading, [chatJid]: false } });
      }
    }
    markChatRead(chatJid).catch(() => undefined);
  }

  async function syncMetadataNow() {
    metadataSyncStarted = false;
    if (!anyConnected) return;
    metadataSyncStarted = true;
    try {
      await syncChatMetadata();
      showToast("Conversas atualizadas", "info");
    } catch (err) {
      metadataSyncStarted = false;
      showToast(err instanceof Error ? err.message : "Falha ao sincronizar", "error");
    }
  }

  async function createTagFromPrompt() {
    const name = window.prompt("Nome da tag:");
    if (!name || !name.trim()) return;
    const color = window.prompt("Cor hex, ex: #108b7c:", "#108b7c") || "#108b7c";
    try {
      await createTag({ name: name.trim(), color });
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Falha ao criar tag", "error");
    }
  }

  async function bulkArchiveVisible(action: "archive" | "unarchive") {
    const archived = action === "archive";
    const jids = chats.map((chat) => chat.chatJid);
    if (!jids.length) return;
    const verb = archived ? "Arquivar" : "Desarquivar";
    const choice = await modal.open({
      kind: archived ? "danger" : "info",
      title: `${verb} ${jids.length} conversa${jids.length > 1 ? "s" : ""}`,
      message: `Confirma ${verb.toLowerCase()} todas as conversas visíveis no filtro atual?`,
      actions: [
        { label: "Cancelar", value: "cancel", variant: "secondary" },
        { label: verb, value: "ok", variant: archived ? "danger" : "primary" },
      ],
    });
    if (choice !== "ok") return;
    try {
      const result = await bulkArchiveChats(jids, archived) as { updated?: number };
      showToast(`${verb}das ${result?.updated ?? jids.length} conversa(s)`, "info");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Falha ao processar lote", "error");
    }
  }

  async function confirmDeleteTag(tag: ChatTag) {
    const choice = await modal.open({
      kind: "danger",
      title: "Remover tag",
      message: `Remover a tag "${tag.name}"?`,
      destructive: true,
      actions: [
        { label: "Cancelar", value: "cancel", variant: "secondary" },
        { label: "Remover", value: "delete", variant: "danger" },
      ],
    });
    if (choice !== "delete") return;
    try {
      await deleteTag(tag.id);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Falha ao remover tag", "error");
    }
  }

  return (
    <aside className="wa-sidebar">
      <header className="wa-sidebar-header">
        <h1 className="wa-sidebar-title">Conversas</h1>
        <div className="wa-sidebar-actions">
          <button className="wa-icon-btn" type="button" title="Nova conversa" aria-label="Nova conversa">
            <SvgIcon svg={icons.newChat} />
          </button>
          <button
            className="wa-icon-btn"
            type="button"
            title="Sincronizar nomes de grupos e contatos"
            aria-label="Sincronizar nomes de grupos e contatos"
            onClick={() => void syncMetadataNow()}
          >
            <SvgIcon svg={icons.menu} />
          </button>
        </div>
      </header>

      <div className="wa-search-wrap">
        <div className="wa-search">
          <span className="wa-search-icon"><SvgIcon svg={icons.search} /></span>
          <input
            className="wa-search-input"
            placeholder="Pesquisar ou comecar uma nova conversa"
            value={state.chatListSearch || ""}
            onChange={(event) => patch({ chatListSearch: event.currentTarget.value })}
          />
        </div>
      </div>

      <FilterChips
        active={activeFilter}
        chats={state.chats || []}
        tags={state.chatTags || []}
        onSelect={(filter) => patch({ chatListFilter: filter })}
        onCreateTag={() => void createTagFromPrompt()}
        onDeleteTag={(tag) => void confirmDeleteTag(tag)}
      />

      {chats.length > 0 && (activeFilter !== "all" || (state.chatListSearch || "").trim()) ? (
        <div className="wa-bulk-bar">
          <span className="wa-bulk-count">{chats.length} conversa{chats.length > 1 ? "s" : ""} visíveis</span>
          {activeFilter === "archived" ? (
            <button
              className="btn btn-secondary btn-compact"
              type="button"
              onClick={() => void bulkArchiveVisible("unarchive")}
            >
              Desarquivar todas
            </button>
          ) : (
            <button
              className="btn btn-secondary btn-compact"
              type="button"
              onClick={() => void bulkArchiveVisible("archive")}
            >
              Arquivar todas
            </button>
          )}
        </div>
      ) : null}

      <div className="wa-chat-items">
        {chats.length ? chats.map((chat) => (
          <ChatListItem
            key={chat.chatJid}
            chat={chat}
            messages={messagesForChatItem(chat)}
            tags={state.chatTags || []}
            active={isActiveChatItem(chat)}
            onOpen={() => void openChat(chat.chatJid)}
            onContextMenu={(event) => {
              event.preventDefault();
              setContextMenu({ chat, x: event.clientX, y: event.clientY });
            }}
          />
        )) : <div className="wa-empty">Nenhuma conversa encontrada.</div>}
      </div>

      {contextMenu ? (
        <ChatContextMenu
          menu={contextMenu}
          tags={state.chatTags || []}
          onClose={() => setContextMenu(null)}
          onError={(message) => showToast(message, "error")}
        />
      ) : null}
    </aside>
  );
}
